import type { MessageCls, MessageMeta, MessageType } from './message'

import { nanoid } from 'nanoid'
import {
  CLASS_BASED,
  createError,
  createEvent,
  createRequest,
  createResponse,
  ERROR,
  EVENT,
  meta,
  REQUEST,
  RESPONSE,
  STOP_IMMEDIATE_PROPAGATION,
  STOP_PROPAGATION,
  stopImmediatePropagation,
} from './message'
import {
  isFunction,
  isNil,
  isObject,
  isString,
  isWithTopic,
  normalize,
  split,
} from './tools'

export type MessageHandler<T = any> = (
  message: T & Readonly<MessageMeta>
) => any

export type HandlerOptions = {
  once?: boolean
  handle?: MessageType
}

export type RequestOptions = {
  timeout?: number
}

export type Subscription = {
  (): void
  unsubscribe(): void
}

interface HandlerRegistration {
  handler: MessageHandler
  handle: MessageType
  once: boolean
}

export class MessageBus {
  private id?: string
  private parent?: MessageBus
  private children = new Map<string, MessageBus>()
  private handlers = new Set<HandlerRegistration>()

  constructor(id?: string) {
    this.id = id || nanoid(10)
  }

  private getChild(name: string): MessageBus {
    let child = this.children.get(name)
    if (!child) {
      child = new MessageBus('__child__')
      child.id = undefined // child buses do not have id, only root (and mounted) buses has an id
      child.parent = this
      this.children.set(name, child)
    }
    return child
  }

  /**
   * Subscribe to events
   */

  // prettier-ignore
  on<T = any>(handler: MessageHandler<T>, options?: HandlerOptions): Subscription

  // prettier-ignore
  on<T = any>(path: string, handler: MessageHandler<T>, options?: HandlerOptions): Subscription

  // prettier-ignore
  on<MsgCls extends MessageCls>(constructor: MsgCls, handler: MessageHandler<InstanceType<MsgCls>>, options?: HandlerOptions): Subscription

  // prettier-ignore
  on<MsgCls extends MessageCls>(path: string, constructor: MsgCls, handler: MessageHandler<InstanceType<MsgCls>>, options?: HandlerOptions): Subscription

  // implementation
  on(...args: unknown[]): Subscription {
    const { path, handler, classBased, options } = this._parseOnArgs(args)
    return this._on(path, handler, classBased, options)
  }

  /**
   * Subscribe to events once (shorthand for on with `once` option)
   */

  // prettier-ignore
  once<T = any>(handler: MessageHandler<T>, options?: HandlerOptions): Subscription

  // prettier-ignore
  once<T = any>(path: string, handler: MessageHandler<T>, options?: HandlerOptions): Subscription

  // prettier-ignore
  once<MsgCls extends MessageCls>(constructor: MsgCls, handler: MessageHandler<InstanceType<MsgCls>>, options?: HandlerOptions): Subscription

  // prettier-ignore
  once<MsgCls extends MessageCls>(path: string, constructor: MsgCls, handler: MessageHandler<InstanceType<MsgCls>>, options?: HandlerOptions): Subscription

  // implementation
  once(...args: unknown[]): Subscription {
    const { path, handler, classBased, options } = this._parseOnArgs(args)
    const normalizedOptions = { ...options, once: true }
    return this._on(path, handler, classBased, normalizedOptions)
  }

  // internal recursive on() implementation
  private _on(
    path: string | undefined | null,
    handler: MessageHandler,
    classBased: boolean = false,
    options: HandlerOptions = {}
  ): Subscription {
    const [childName, restPath] = split(path)

    if (childName) {
      const child = this.getChild(childName)
      return child._on(restPath, handler, classBased, options)
    }

    let handle = options?.handle ?? EVENT
    if (classBased) handle |= CLASS_BASED

    const registration: HandlerRegistration = {
      handler,
      handle,
      once: options?.once ?? false,
    }

    this.handlers.add(registration)

    const subscription = () => {
      this.handlers.delete(registration)
    }

    return (subscription.unsubscribe = subscription)
  }

  private _parseOnArgs(args: unknown[]): {
    path: string | undefined
    handler: MessageHandler
    classBased: boolean
    options: HandlerOptions
  } {
    let path: string | undefined
    let constructor: MessageCls | undefined
    let handler: MessageHandler | undefined
    let options: HandlerOptions | undefined

    let i = 0

    // first arg is optionally a path
    if (isString(args[i])) {
      path = args[i++] as string
    }

    // next arg is optionally a class with a topic
    if (isFunction(args[i]) && isWithTopic(args[i] as Function)) {
      constructor = args[i++] as MessageCls
    }

    // next must be the handler
    if (isFunction(args[i])) {
      handler = args[i++] as MessageHandler
    } else {
      throw new Error('Invalid arguments: handler is required')
    }

    // last arg is optionally options
    if (isObject(args[i])) {
      options = args[i++] as HandlerOptions
    }

    // if there are leftover args, something is wrong
    if (i !== args.length) {
      throw new Error('Invalid arguments to on()/once()')
    }

    let pathFinal: string | undefined
    if (constructor) {
      const topic = normalize(constructor.topic)
      pathFinal = path ? normalize(path) + '/' + topic : topic
    } else {
      pathFinal = path
    }

    return {
      path: pathFinal ? normalize(pathFinal) : undefined,
      handler,
      classBased: !!constructor,
      options: options ?? {},
    }
  }

  /**
   * Emit an event
   */
  emit(): void
  emit(path: string, payload?: any): void
  emit(payload: any): void

  // implementation
  emit(arg1?: unknown, arg2?: unknown): void {
    // -> emit()
    if (isNil(arg1)) {
      const message = createEvent('', arg1)
      return this._emit(undefined, message)
    }

    if (isString(arg1)) {
      const path = normalize(arg1)

      // -> emit(path, instance)
      if (isObject(arg2)) {
        const Cls = arg2.constructor
        if (isWithTopic(Cls)) {
          const topic = normalize(Cls.topic)
          const message = createEvent(undefined, arg2)
          return this._emit(path + '/' + topic, message)
        }
      }

      // -> emit(path, payload)
      const message = createEvent(path, arg2)
      return this._emit(path, message)
    }

    if (isObject(arg1)) {
      // class instance payload with static topic
      const Cls = arg1.constructor
      if (isWithTopic(Cls)) {
        const path = normalize(Cls.topic)
        const message = createEvent(undefined, arg1)
        return this._emit(path, message)
      }

      // plain object payload with topic property
      if (isWithTopic(arg1)) {
        const path = normalize(arg1.topic)
        const message = createEvent(path, arg1)
        return this._emit(path, message)
      }
    }

    // -> emit(payload)
    const message = createEvent('', arg1)
    return this._emit(undefined, message)
  }

  // internal recursive emit() implementation
  private _emit(path: string | undefined, message: MessageMeta): void {
    const [childName, restPath] = split(path)

    if (childName) {
      const child = this.getChild(childName)
      return child._emit(restPath, message)
    }

    this._trigger(message)
  }

  private _emitAsync(path: string | undefined, message: MessageMeta): void {
    Promise.resolve().then(() => this._emit(path, message))
  }

  // internal recursive trigger implementation for bubbling events to parent buses
  private _trigger(message: MessageMeta): void {
    const handlersToRemove: HandlerRegistration[] = []
    for (const reg of this.handlers) {
      const [type, topic] = message[meta]

      // stop immediate propagation if flag is set
      if (type & STOP_IMMEDIATE_PROPAGATION) {
        break
      }

      // skip handlers that do not match message type
      // message type must include all bits set in handler's handle bitmask
      if ((type & reg.handle) !== reg.handle) {
        continue
      }

      let result: any
      try {
        result = reg.handler(message)
      } catch (error) {
        if (type & ERROR) {
          console.error('[mqbw] Error in error handler:', String(error)) // avoid infinite loop
        } else {
          const errorMessage = createError('', error, message)
          this._emitAsync(undefined, errorMessage)
        }
      }

      if (reg.once) {
        handlersToRemove.push(reg)
      }

      // if this was a request message and the handler returned a value, send a response
      if (type & REQUEST && result !== undefined) {
        stopImmediatePropagation(message) // request considered handled
        Promise.resolve(result)
          .then((value) => {
            const responseMessage = createResponse(topic, value, message)
            this._emit(undefined, responseMessage)
          })
          .catch((error) => {
            const errorMessage = createError('', error, message)
            this._emit(undefined, errorMessage)
          })
      }
    }

    for (const reg of handlersToRemove) {
      this.handlers.delete(reg)
    }

    const [type] = message[meta]
    if (type & (STOP_PROPAGATION | STOP_IMMEDIATE_PROPAGATION)) {
      return
    }

    if (this.parent) {
      this.parent._trigger(message)
    }

    if (this.id) {
      const [finalType, topic] = message[meta]
      if (
        finalType & REQUEST &&
        !(finalType & (STOP_PROPAGATION | STOP_IMMEDIATE_PROPAGATION))
      ) {
        // unhandled request, send error response
        const error = 'Unhandled request'
        const errorMessage = createError('', { error }, message)
        this._emitAsync(topic, errorMessage)
      }
    }
  }

  /**
   * Request-response pattern
   */

  // prettier-ignore
  async request<T = any>(): Promise<T & MessageMeta>

  // prettier-ignore
  async request<T = any>(path: string, payload?: any, options?: RequestOptions): Promise<T & MessageMeta>

  // prettier-ignore
  async request<T = any>(payload: any, options?: RequestOptions): Promise<T & MessageMeta>

  // implementation
  async request<T = any>(
    arg1?: unknown,
    arg2?: unknown,
    arg3?: unknown
  ): Promise<T & MessageMeta> {
    // -> request()
    if (isNil(arg1)) {
      const request = createRequest('', arg1)
      return this._request(undefined, request)
    }

    if (isString(arg1)) {
      const path = normalize(arg1)

      // -> request(path, instance, options)
      if (isObject(arg2)) {
        const Cls = arg2.constructor
        if (isWithTopic(Cls)) {
          const topic = normalize(Cls.topic)
          const request = createRequest(undefined, arg2)
          return this._request(
            path + '/' + topic,
            request,
            arg3 as RequestOptions
          )
        }
      }

      // -> request(path, payload, options)
      const request = createRequest(path, arg2)
      return this._request(path, request, arg3 as RequestOptions)
    }

    if (isObject(arg1)) {
      // class instance payload with static topic
      const Cls = arg1.constructor
      if (isWithTopic(Cls)) {
        const path = normalize(Cls.topic)
        const request = createRequest(undefined, arg1)
        return this._request(path, request, arg2 as RequestOptions)
      }

      // plain object payload with topic property
      if (isWithTopic(arg1)) {
        const path = normalize(arg1.topic)
        const request = createRequest(path, arg1)
        return this._request(path, request, arg2 as RequestOptions)
      }
    }

    // -> request(payload, options)
    const request = createRequest('', arg1)
    return this._request(undefined, request, arg2 as RequestOptions)
  }

  private async _request<T = any>(
    path: string | undefined,
    request: MessageMeta,
    options: RequestOptions = {}
  ): Promise<T> {
    const [childName, restPath] = split(path)

    if (childName) {
      const child = this.getChild(childName)
      return child._request(restPath, request, options)
    }

    const [, , requestId] = request[meta]
    const timeout = options?.timeout ?? 5000

    return new Promise<T>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      let unsubscribeResponse: (() => void) | undefined
      let unsubscribeError: (() => void) | undefined

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId)
        if (unsubscribeResponse) unsubscribeResponse()
        if (unsubscribeError) unsubscribeError()
      }

      timeoutId = setTimeout(() => {
        cleanup()
        const error = 'Request timed out'
        const errorMessage = createError('', { error }, request)
        reject(errorMessage)
      }, timeout)

      unsubscribeResponse = this._on(
        undefined,
        (response) => {
          const [, , id] = response[meta]
          if (id !== requestId) return
          cleanup()
          resolve(response)
        },
        false,
        { handle: RESPONSE }
      )

      unsubscribeError = this._on(
        undefined,
        (error) => {
          const [, , id] = error[meta]
          if (id !== requestId) return
          cleanup()
          reject(error)
        },
        false,
        { handle: ERROR }
      )

      this._emit(undefined, request)
    })
  }

  mount(path: string, bus: MessageBus): Subscription {
    const [childName, restPath] = split(path)

    if (childName && restPath) {
      const child = this.getChild(childName)
      return child.mount(restPath, bus)
    }

    if (!childName) {
      throw new Error('Invalid path to mount child bus')
    }

    if (this.children.has(childName)) {
      throw new Error(`Cannot mount: bus already exists at path '${childName}'`)
    }

    bus.parent = this
    this.children.set(childName, bus)

    const subscription = () => {
      bus.parent = undefined
      this.children.delete(childName)
    }

    return (subscription.unsubscribe = subscription)
  }

  unmount(path: string): void {
    const [childName, restPath] = split(path)
    if (!childName) return

    const child = this.children.get(childName)
    if (!child) return

    if (restPath) {
      child.unmount(restPath)
    } else {
      child.parent = undefined
      this.children.delete(childName)
    }
  }

  /**
   * Return bus tree debug snapshot
   */
  debug(indent: string = '', root = true): string {
    let result = ''

    if (root) {
      result += `/ → ${this.handlers.size} handlers\n`
    }

    const children = [...this.children.entries()] //
      .toSorted(([nameA], [nameB]) => nameA.localeCompare(nameB))

    for (let i = 0; i < children.length; i++) {
      const [name, child] = children[i]
      const last = i === children.length - 1

      result += `${indent}${last ? '└' : '├'}─ /${name} (${child.id ? 'mount' : 'child'}) → ${child.handlers.size} handlers\n`
      result += child.debug(indent + (last ? '   ' : '│  '), false)
    }

    return result
  }
}
